import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/sections/Footer";

const LAST_UPDATED = "August 4, 2026";
const COMPANY = "JDTECH / JD Technologies";
const SITE = "https://jdtechnologies.co";
const EMAIL = "contact@jdtechnologies.co";
const ADDRESS = "FCA 143 BLOCK-C SGM NAGAR FARIDABAD, HARYANA 121001, India";

const sections = [
  { id: "introduction", label: "1. Introduction" },
  { id: "information-we-collect", label: "2. Information We Collect" },
  { id: "meta-facebook", label: "3. Meta / Facebook Platforms" },
  { id: "how-we-use", label: "4. How We Use Information" },
  { id: "sharing", label: "5. Sharing & Disclosure" },
  { id: "retention", label: "6. Data Retention" },
  { id: "deletion", label: "7. Data Deletion & Your Rights" },
  { id: "security", label: "8. Security" },
  { id: "cookies", label: "9. Cookies & Tracking" },
  { id: "children", label: "10. Children's Privacy" },
  { id: "international", label: "11. International Transfers" },
  { id: "changes", label: "12. Changes to This Policy" },
  { id: "contact", label: "13. Contact Us" },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title =
      "Privacy Policy — JDTECH | jdtechnologies.co";
  }, []);

  return (
    <main className="relative min-h-screen bg-black text-white">
      <div className="absolute inset-x-0 top-0 h-[420px] pointer-events-none bg-[radial-gradient(900px_420px_at_50%_-10%,rgba(10,132,255,0.14),transparent_70%)]" />

      <header className="relative z-10 border-b border-white/5">
        <div className="max-w-[900px] mx-auto px-5 sm:px-6 md:px-10 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} />
            Back to home
          </Link>
          <Link
            to="/"
            className="inline-flex items-baseline gap-[3px] select-none"
          >
            <span className="font-display text-[18px] font-black tracking-[-0.04em] text-white leading-none">
              JD
            </span>
            <span className="font-display text-[18px] font-light tracking-[0.32em] text-white/85 leading-none uppercase">
              tech
            </span>
          </Link>
        </div>
      </header>

      <article className="relative z-10 max-w-[900px] mx-auto px-5 sm:px-6 md:px-10 pt-14 md:pt-20 pb-20">
        <p className="text-[11px] uppercase tracking-[0.28em] text-[#6b6b70] mb-4">
          Legal
        </p>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white">
          Privacy Policy
        </h1>
        <p className="mt-4 text-white/55 text-sm md:text-base max-w-2xl leading-relaxed">
          This Privacy Policy describes how {COMPANY} (&quot;JDTECH,&quot; &quot;we,&quot;
          &quot;us,&quot; or &quot;our&quot;) collects, uses, shares, and protects
          information when you use {SITE}, our products, services, and
          applications that integrate with Meta platforms (including Facebook,
          Instagram, WhatsApp, and Messenger APIs).
        </p>
        <p className="mt-3 text-xs text-white/40">
          Last updated: {LAST_UPDATED}
        </p>

        <nav
          aria-label="Privacy policy sections"
          className="mt-10 p-5 md:p-6 rounded-2xl border border-white/8 bg-white/[0.02]"
        >
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#6b6b70] mb-3">
            Contents
          </p>
          <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {sections.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="text-white/65 hover:text-[#0A84FF] transition-colors"
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="mt-14 space-y-14 text-[15px] leading-relaxed text-white/70">
          <section id="introduction" className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold text-white tracking-tight mb-4">
              1. Introduction
            </h2>
            <p>
              We respect your privacy and are committed to protecting personal
              data in accordance with applicable laws (including the Information
              Technology Act, 2000 and related rules in India, and—where
              applicable—GDPR and other regional privacy frameworks) and with{" "}
              <a
                href="https://developers.facebook.com/terms/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0A84FF] hover:underline"
              >
                Meta Platform Terms
              </a>
              ,{" "}
              <a
                href="https://developers.facebook.com/docs/development/release/data-protection"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0A84FF] hover:underline"
              >
                Meta Developer Policies
              </a>
              , and Meta&apos;s{" "}
              <a
                href="https://www.facebook.com/privacy/policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0A84FF] hover:underline"
              >
                Privacy Policy
              </a>
              .
            </p>
            <p className="mt-3">
              By accessing our website or using our services (including those
              that connect to Meta APIs), you acknowledge this Privacy Policy. If
              you do not agree, please do not use our services.
            </p>
          </section>

          <section id="information-we-collect" className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold text-white tracking-tight mb-4">
              2. Information We Collect
            </h2>
            <p className="mb-3">We may collect the following categories of information:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <span className="text-white/90">Contact &amp; business information</span>{" "}
                — name, email address, phone number, company name, and messages
                you send us (for example via mailto or contact forms).
              </li>
              <li>
                <span className="text-white/90">Technical &amp; usage data</span>{" "}
                — IP address, browser type, device information, pages visited,
                referring URLs, and approximate location derived from IP, collected
                through server logs or analytics tools.
              </li>
              <li>
                <span className="text-white/90">Account &amp; authentication data</span>{" "}
                — identifiers and tokens needed to operate logged-in features or
                third-party integrations you authorize.
              </li>
              <li>
                <span className="text-white/90">Meta platform data</span> — data
                obtained via Meta products and APIs when you (or your
                organization) authorize our app, as described in Section 3.
              </li>
            </ul>
          </section>

          <section id="meta-facebook" className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold text-white tracking-tight mb-4">
              3. Meta / Facebook Platforms &amp; APIs
            </h2>
            <p>
              Our applications and client projects may integrate with Meta
              technologies, including but not limited to Facebook Login,
              Facebook Graph API, Instagram Graph API, WhatsApp Business Platform
              / Cloud API, Messenger Platform, Meta Marketing API, and related
              SDKs. This section is intended to satisfy Meta&apos;s requirements
              for a clear, publicly available privacy policy covering use of
              Platform Data.
            </p>

            <h3 className="mt-6 text-lg font-semibold text-white/95">
              3.1 Data we may receive from Meta
            </h3>
            <p className="mt-2">
              Subject to the permissions you grant and the products you use, we
              may receive:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                Basic profile information (e.g. name, profile picture, user ID)
                via Facebook Login or similar auth flows.
              </li>
              <li>
                Page, Business, Ad Account, Catalog, or Instagram Business
                Account metadata and content you authorize us to access.
              </li>
              <li>
                Messaging metadata and message content necessary to provide
                WhatsApp / Messenger customer communication features you enable.
              </li>
              <li>
                Insights, engagement, and advertising performance metrics for
                accounts you connect.
              </li>
              <li>
                Access tokens and webhook payloads required to operate the
                integration securely.
              </li>
            </ul>
            <p className="mt-3">
              We only request permissions that are reasonably necessary for the
              features we provide. We do not access data beyond the scopes you
              approve in Meta&apos;s authorization dialogs.
            </p>

            <h3 className="mt-6 text-lg font-semibold text-white/95">
              3.2 How we use Meta Platform Data
            </h3>
            <p className="mt-2">We use Meta Platform Data solely to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Authenticate users and link Meta accounts to our services.</li>
              <li>
                Deliver the features you requested (e.g. publishing, inbox /
                messaging tools, analytics dashboards, ad management helpers).
              </li>
              <li>Maintain, secure, and troubleshoot our integrations.</li>
              <li>
                Comply with Meta Platform Terms, applicable law, and valid legal
                requests.
              </li>
            </ul>

            <h3 className="mt-6 text-lg font-semibold text-white/95">
              3.3 What we do not do with Meta data
            </h3>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                We do <span className="text-white/90">not sell</span> Meta
                Platform Data to third parties.
              </li>
              <li>
                We do not use Platform Data to build or enhance independent
                user profiles for unrelated advertising or surveillance.
              </li>
              <li>
                We do not transfer Platform Data to another app, service, or
                third party except as needed to operate the service you
                requested, with appropriate contractual protections, or as
                required by law.
              </li>
              <li>
                We do not use Platform Data in ways prohibited by Meta&apos;s
                Platform Terms or Developer Policies.
              </li>
            </ul>

            <h3 className="mt-6 text-lg font-semibold text-white/95">
              3.4 Meta&apos;s role
            </h3>
            <p className="mt-2">
              Meta Platforms, Inc. and its affiliates process data according to
              their own policies. When you use Facebook, Instagram, WhatsApp, or
              related products, Meta&apos;s terms and privacy policy also apply.
              We are not responsible for Meta&apos;s independent processing.
            </p>
          </section>

          <section id="how-we-use" className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold text-white tracking-tight mb-4">
              4. How We Use Information
            </h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Provide, operate, and improve our website and services.</li>
              <li>Respond to inquiries and deliver client engagements.</li>
              <li>Authenticate users and authorize third-party integrations.</li>
              <li>Monitor security, prevent fraud and abuse, and debug issues.</li>
              <li>
                Send service-related communications (and marketing only where
                permitted and with an easy opt-out).
              </li>
              <li>Comply with legal obligations and enforce our agreements.</li>
            </ul>
          </section>

          <section id="sharing" className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold text-white tracking-tight mb-4">
              5. Sharing &amp; Disclosure
            </h2>
            <p className="mb-3">We may share information with:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <span className="text-white/90">Service providers</span> —
                hosting, analytics, email, and infrastructure vendors who process
                data on our instructions (e.g. cloud hosting such as Firebase /
                Google Cloud).
              </li>
              <li>
                <span className="text-white/90">Meta and other platforms</span> —
                as necessary to complete API calls you authorize.
              </li>
              <li>
                <span className="text-white/90">Professional advisors &amp; authorities</span>{" "}
                — when required by law, regulation, legal process, or to protect
                rights, safety, and security.
              </li>
              <li>
                <span className="text-white/90">Business transfers</span> — in
                connection with a merger, acquisition, or asset sale, subject to
                continued confidentiality commitments.
              </li>
            </ul>
            <p className="mt-3">
              We do not sell personal information. We do not share Meta Platform
              Data for cross-context behavioral advertising unrelated to the
              service you requested.
            </p>
          </section>

          <section id="retention" className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold text-white tracking-tight mb-4">
              6. Data Retention
            </h2>
            <p>
              We retain personal data only as long as needed for the purposes
              described in this policy, to fulfill contractual obligations, or as
              required by law. Meta Platform Data is retained only while your
              integration remains active or as needed to provide the feature you
              requested, then deleted or anonymized within a reasonable period
              (and in any event in line with Meta&apos;s deletion and retention
              requirements) unless a longer retention is legally required.
            </p>
          </section>

          <section id="deletion" className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold text-white tracking-tight mb-4">
              7. Data Deletion &amp; Your Rights
            </h2>
            <p>
              Depending on your location, you may have rights to access, correct,
              update, restrict, or delete your personal data, and to object to
              certain processing or request portability.
            </p>

            <h3 className="mt-6 text-lg font-semibold text-white/95">
              7.1 How to request deletion
            </h3>
            <p className="mt-2">
              To request deletion of your data associated with JDTECH or any
              Meta-connected app we operate, email{" "}
              <a
                href={`mailto:${EMAIL}?subject=Data%20Deletion%20Request`}
                className="text-[#0A84FF] hover:underline"
              >
                {EMAIL}
              </a>{" "}
              with the subject line &quot;Data Deletion Request&quot; and include:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Your full name and the email associated with your account.</li>
              <li>
                The Meta / Facebook / Instagram / WhatsApp user ID or Page ID,
                if applicable.
              </li>
              <li>A clear description of the data or account to delete.</li>
            </ul>
            <p className="mt-3">
              We will acknowledge your request within a reasonable time (typically
              within 7 business days) and complete deletion of personal data under
              our control within 30 days, except where retention is required by
              law or for legitimate security / dispute-resolution purposes. We
              will also revoke related Meta access tokens where applicable.
            </p>

            <h3 className="mt-6 text-lg font-semibold text-white/95">
              7.2 Removing our app from Facebook
            </h3>
            <p className="mt-2">
              You may also remove our app&apos;s access at any time via Facebook
              Settings →{" "}
              <a
                href="https://www.facebook.com/settings?tab=applications"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0A84FF] hover:underline"
              >
                Apps and Websites
              </a>
              . Removing access stops future data sharing from Meta; you should
              still contact us if you want data already stored by us to be
              deleted.
            </p>

            <h3 className="mt-6 text-lg font-semibold text-white/95">
              7.3 Meta User Data Deletion Callback
            </h3>
            <p className="mt-2">
              Where we operate a Meta app that receives user data, we support
              Meta&apos;s user data deletion requirements. When Meta sends a
              deletion request (for example via a signed deletion callback), we
              process it to delete associated user data and return a confirmation
              status URL or code as required by Meta&apos;s documentation.
            </p>
          </section>

          <section id="security" className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold text-white tracking-tight mb-4">
              8. Security
            </h2>
            <p>
              We implement appropriate technical and organizational measures to
              protect personal data, including encrypted transport (HTTPS),
              access controls, least-privilege practices for API tokens, and
              secure storage of credentials. No method of transmission or storage
              is 100% secure; we cannot guarantee absolute security.
            </p>
          </section>

          <section id="cookies" className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold text-white tracking-tight mb-4">
              9. Cookies &amp; Tracking
            </h2>
            <p>
              Our website may use cookies, local storage, or similar technologies
              for essential site functionality, preferences, and analytics. You
              can control cookies through your browser settings. Third-party
              services we use (including Meta Pixel or similar tools, if enabled
              on a given property) may set their own cookies subject to their
              policies.
            </p>
          </section>

          <section id="children" className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold text-white tracking-tight mb-4">
              10. Children&apos;s Privacy
            </h2>
            <p>
              Our services are directed to businesses and adults. We do not
              knowingly collect personal information from children under 13 (or
              the minimum age required in your jurisdiction). If you believe a
              child has provided us data, contact us and we will take steps to
              delete it.
            </p>
          </section>

          <section id="international" className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold text-white tracking-tight mb-4">
              11. International Transfers
            </h2>
            <p>
              We are based in India and may process data on servers or with
              providers located in other countries (including the United States
              and other regions where Meta or our cloud providers operate). Where
              required, we use appropriate safeguards for cross-border transfers.
            </p>
          </section>

          <section id="changes" className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold text-white tracking-tight mb-4">
              12. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. The
              &quot;Last updated&quot; date at the top will reflect the latest
              revision. Material changes will be posted on this page. Continued
              use of our services after changes constitutes acceptance of the
              updated policy.
            </p>
          </section>

          <section id="contact" className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold text-white tracking-tight mb-4">
              13. Contact Us
            </h2>
            <p>
              For privacy questions, access requests, or deletion requests,
              contact:
            </p>
            <div className="mt-4 p-5 rounded-2xl border border-white/8 bg-white/[0.02] text-white/80 space-y-1">
              <p className="font-display font-semibold text-white">{COMPANY}</p>
              <p>{ADDRESS}</p>
              <p>
                Email:{" "}
                <a
                  href={`mailto:${EMAIL}`}
                  className="text-[#0A84FF] hover:underline"
                >
                  {EMAIL}
                </a>
              </p>
              <p>
                Website:{" "}
                <a
                  href={SITE}
                  className="text-[#0A84FF] hover:underline"
                >
                  {SITE}
                </a>
              </p>
              <p className="pt-2 text-sm text-white/50">
                Privacy Policy URL (for Meta App Review):{" "}
                <a
                  href={`${SITE}/privacy`}
                  className="text-[#0A84FF] hover:underline"
                >
                  {SITE}/privacy
                </a>
              </p>
            </div>
          </section>
        </div>
      </article>

      <Footer />
    </main>
  );
}
